'use client'

import { apiV2, userApi } from '@/shared/api/api'
import type { ApiRequestConfig } from '@/shared/api/axios'
import { getUserApiV2Url } from '@/shared/constants/apiUrlConstants'
import { CART_API_PATHS } from '@/shared/constants/clientApiPaths'
import { isValidSixDigitPin } from '@/shared/hooks/usePincodeLookup'
import { authServices } from '@/shared/services/auth.services'

const SESSION_ID_KEY = 'exaltride-guest-session'

export type CartRequestOptions = {
  silent?: boolean
  /**
   * The shopper's *browsing* pincode, which the backend uses to resolve each
   * line's `delivery`. Provisional, never binding — checkout re-prices against
   * the delivery address instead, so its calls deliberately pass nothing here.
   */
  pincode?: string | null
}

export function getOrCreateSessionId(): string {
  if (typeof window === 'undefined') return ''

  let sessionId = localStorage.getItem(SESSION_ID_KEY)
  if (!sessionId) {
    sessionId = `guest_${crypto.randomUUID()}`
    localStorage.setItem(SESSION_ID_KEY, sessionId)
  }
  return sessionId
}

export function clearSessionId(): void {
  if (typeof window !== 'undefined') {
    localStorage.removeItem(SESSION_ID_KEY)
  }
}

function getCartHeaders(): Record<string, string> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  }

  if (!authServices.isAuthenticated()) {
    headers['X-Session-ID'] = getOrCreateSessionId()
  }

  return headers
}

function getCartV2Config(options?: CartRequestOptions): ApiRequestConfig {
  const headers = getCartHeaders()
  if (options?.silent) {
    headers.skipToast = 'true'
  }
  const config: ApiRequestConfig = {
    baseURL: getUserApiV2Url(),
    headers,
    authTokenType: authServices.isAuthenticated() ? 'accessToken' : false,
  }
  // Omitted entirely unless it's a real pincode — an absent param is the
  // backend's "no destination" signal, which it answers with `delivery: null`.
  // An empty string would not read the same way.
  if (isValidSixDigitPin(options?.pincode)) {
    config.params = { pincode: options!.pincode }
  }
  return config
}

export interface InstallationPackage {
  id: string
  name: string
  description: string
  price: number
  timeTaken: string
}

export type CartDeliveryMode = 'standard' | 'express'

/**
 * The fastest option reachable at the pincode the cart was fetched with —
 * Express when the line offers it there, otherwise Standard. Null when no
 * pincode was sent, since there is no destination to compute against.
 */
export interface CartItemDelivery {
  mode: CartDeliveryMode
  estimatedDeliveryDays: number
  expressAvailable: boolean
  /** Echoed back, so a stale persisted value can be told from a current one. */
  pincode: string
}

export interface CartItem {
  productId: string
  variantId?: string | null
  quantity: number
  addedAt?: string
  updatedAt?: string
  cartId?: string
  delivery?: CartItemDelivery | null
  installation: { id: string; name: string; price: number; timeTaken: string } | null
  installationAvailable: boolean
  availableInstallationPackages: InstallationPackage[]
  selectedOptions?: Record<string, string> | null
  productDetails: {
    id: string
    slug: string
    title: string
    sku: string
    primary_image: string
    price: number
    compare_at_price: number
    discount_percentage: string
    in_stock: boolean
    stock: number
    brand_name: string
    /** When true, cart line shows masked selling price and hides the discount badge. */
    hide_coupon_section?: boolean
  }
}

export type CartMeta = {
  total_items: number
  subtotal: string
  currency: string
  isDiscountApplicable: boolean
  discountAmount: number
  couponCode: string | null
  installation_total: number
  /** Always 0 on the cart — express is only charged from checkout onwards. */
  shipping_fee: number
  final_total: number
  /** MRP discount + coupon discount, combined server-side. */
  total_savings: number
  /** Combined cashback across every item in the cart. */
  total_cashback: number
  delivery?: { pincode: string | null; express_available: boolean } | null
}

export type CartResponse = {
  data: CartItem[]
  meta?: CartMeta
  message?: string
  couponApplied?: boolean
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function toNumber(value: unknown, fallback: number = 0): number {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string') {
    const parsed = Number(value)
    if (Number.isFinite(parsed)) return parsed
  }
  return fallback
}

function normalizeCartMeta(value: unknown): CartMeta | undefined {
  if (!isRecord(value)) return undefined

  const totalItems = toNumber(value.total_items)
  const subtotal =
    typeof value.subtotal === 'string'
      ? value.subtotal
      : toNumber(value.subtotal).toFixed(2)
  const currency = typeof value.currency === 'string' ? value.currency : 'INR'
  const discountAmount = toNumber(value.coupon_discount ?? value.discountAmount)
  const isDiscountApplicable =
    typeof value.isDiscountApplicable === 'boolean'
      ? value.isDiscountApplicable
      : discountAmount > 0
  const couponCode =
    typeof value.coupon_code === 'string' && value.coupon_code.trim() !== ''
      ? value.coupon_code.trim()
      : null

  const installationTotal = toNumber(value.installation_total)
  const shippingFee = toNumber(value.shipping_fee)
  const finalTotal = toNumber(value.final_total ?? value.subtotal)
  const totalSavings = toNumber(value.total_savings)
  const totalCashback = toNumber(value.total_cashback)

  const rawDelivery = value.delivery
  const delivery = isRecord(rawDelivery)
    ? {
        pincode: typeof rawDelivery.pincode === 'string' ? rawDelivery.pincode : null,
        express_available: !!rawDelivery.express_available,
      }
    : null

  return {
    total_items: totalItems,
    subtotal,
    currency,
    discountAmount,
    isDiscountApplicable,
    couponCode,
    installation_total: installationTotal,
    shipping_fee: shippingFee,
    final_total: finalTotal,
    total_savings: totalSavings,
    total_cashback: totalCashback,
    delivery,
  }
}

/**
 * `delivery` reaches the store through a bare cast otherwise, so an unexpected
 * `mode` would flow straight into the union and out to the UI. Parse it here —
 * the one boundary the wire crosses — and let everything downstream trust it.
 */
function normalizeCartItemDelivery(value: unknown): CartItemDelivery | null {
  if (!isRecord(value)) return null

  const mode = value.mode
  if (mode !== 'standard' && mode !== 'express') return null

  const pincode = value.pincode
  if (typeof pincode !== 'string' || pincode === '') return null

  const days = value.estimatedDeliveryDays ?? value.estimated_delivery_days
  if (days === undefined || days === null) return null

  return {
    mode,
    estimatedDeliveryDays: toNumber(days),
    expressAvailable: !!(value.expressAvailable ?? value.express_available),
    pincode,
  }
}

/**
 * Spread-and-override, never a fresh literal — `carId`, `selectedOptions`,
 * `availableInstallationPackages` and anything the BE adds later must ride
 * through untouched.
 */
function withNormalizedDelivery(items: unknown[]): CartItem[] {
  return items.map((item) =>
    isRecord(item)
      ? ({ ...item, delivery: normalizeCartItemDelivery(item.delivery) } as unknown as CartItem)
      : (item as CartItem),
  )
}

function normalizeCartResponse(payload: unknown): CartResponse {
  if (Array.isArray(payload)) {
    return { data: withNormalizedDelivery(payload) }
  }

  if (!isRecord(payload)) {
    return { data: [] }
  }

  const rawData = payload.data
  const nestedItems = Array.isArray(rawData)
    ? rawData
    : isRecord(rawData) && Array.isArray(rawData.items)
      ? rawData.items
      : isRecord(rawData) && Array.isArray(rawData.result)
        ? rawData.result
        : Array.isArray(payload.result)
          ? payload.result
          : Array.isArray(payload.items)
            ? payload.items
            : []

// Check data.meta first because it is present there in the response
  const meta =
    (isRecord(rawData) ? normalizeCartMeta(rawData.meta) : undefined) ??
    normalizeCartMeta(payload.meta)

  const couponApplied =
    (isRecord(rawData) && rawData.couponApplied === true) || payload.couponApplied === true

  return {
    data: withNormalizedDelivery(nestedItems),
    meta,
    message: typeof payload.message === 'string' ? payload.message : undefined,
    couponApplied,
  }
}

export async function fetchCart(options?: CartRequestOptions): Promise<CartResponse> {
  const response = await apiV2.get<unknown>(CART_API_PATHS.cart, getCartV2Config(options))
  return normalizeCartResponse(response)
}

export type AddToCartPayload = {
  productId: string
  quantity?: number
  variantId?: string | null
  /**
   * Catalog car id (`cars.id`, === `compatibleCars[].id`) the product is being
   * bought for, from "Select Your Car". Sent per item so the vendor can see the
   * car per product. Field name is provisional — BE contract likely renames this
   * to `compatibleCarId`. (Inert until the BE adds the cart_items column.)
   */
  carId?: string | null
}

export async function addToCart(
  payload: AddToCartPayload,
  options?: { silent?: boolean },
): Promise<CartResponse> {
  const { productId, quantity = 1, variantId, carId } = payload
  const body: {
    productId: string
    quantity: number
    variantId?: string
    carId?: string
  } = {
    productId,
    quantity,
  }
  if (variantId) {
    body.variantId = variantId
  }
  if (carId) {
    body.carId = carId
  }

  const response = await apiV2.post<unknown>(CART_API_PATHS.items, body, getCartV2Config(options))
  return normalizeCartResponse(response)
}

export async function updateCartItem(
  productId: string,
  quantity: number,
  options?: { variantId?: string | null; installationPackageId?: string | null },
): Promise<CartResponse> {
  const body: { productId: string; quantity: number; variantId?: string; installationPackageId?: string | null } = {
    productId,
    quantity,
  }
  if (options?.variantId) {
    body.variantId = options.variantId
  }
  if (options && 'installationPackageId' in options) {
    body.installationPackageId = options.installationPackageId ?? null
  }
  const response = await apiV2.put<unknown>(
    CART_API_PATHS.items,
    body,
    getCartV2Config(),
  )
  return normalizeCartResponse(response)
}

export async function removeCartItem(
  productId: string,
  options?: { variantId?: string | null },
): Promise<CartResponse> {
  const body: { productId: string; variantId?: string } = { productId }
  if (options?.variantId) {
    body.variantId = options.variantId
  }
  const response = await apiV2.delete<unknown>(CART_API_PATHS.items, {
    ...getCartV2Config(),
    data: body,
  })
  return normalizeCartResponse(response)
}

export async function clearCart(): Promise<void> {
  await apiV2.delete(CART_API_PATHS.cart, getCartV2Config())
}

export async function mergeCart(): Promise<CartResponse> {
  const sessionId = getOrCreateSessionId()
  const response = await userApi.post<unknown>(
    CART_API_PATHS.merge,
    {},
    { headers: { ...getCartHeaders(), 'X-Session-ID': sessionId } },
  )
  return normalizeCartResponse(response)
}